import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';
import { StyledFlex } from 'src/components/StyledFlex';
import { StyledTitle } from 'src/Superadmin/index.styles';
import CountryPicker, {
  CountryPickerOption,
} from 'src/components/CountryPicker';
import OptimizedUsersTable from './UsersTable/OptimizedUsersTable';
import { ContentProps } from '../index.types';
import { useNavigate } from 'react-router-dom';
import { useBranchesWithCountry } from 'src/utils/branches';

export default function Users(props: ContentProps) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { branches } = useBranchesWithCountry();
  // country-multiselect-picker (R-19): a list of ISO codes, not a single
  // value -- EC-5 requires sending no `countries` param at all when this is
  // empty (see OptimizedUsersTable.tsx, which owns the actual request).
  const [countries, setCountries] = useState<string[]>([]);

  const handleNewUserClick = () => navigate('/superadmin/users/create-user');

  // Only real country codes are offered here -- unlike Structure's tree
  // endpoint, C07's `organization-members` filter has no "no country"
  // bucket to query for (only a literal `Country.code` lookup), so a "Bez
  // kraju" row-filter option would silently return zero rows every time.
  // See OptimizedUsersTable.tsx for the (client-side only) "Bez kraju"
  // column grouping.
  const pickerOptions: CountryPickerOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    branches.forEach((branch) => {
      if (branch.country_code && !seen.has(branch.country_code)) {
        seen.set(
          branch.country_code,
          branch.country_name || branch.country_code
        );
      }
    });
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [branches]);

  // No minimum width here: the tables scroll sideways inside their own
  // panels (OptimizedUsersTable.tsx), so the tab can follow the window. A
  // 1000px floor pushed the whole tab, panels included, past the right edge
  // of windows narrower than about 1075px (e.g. half of a 1920px screen).
  return (
    <Box>
      <StyledFlex>
        <StyledTitle sx={{ fontSize: '28px' }}>
          {t('superadmin.settings.tabs.users.title')}
        </StyledTitle>
        <Button onClick={handleNewUserClick}>
          {t('superadmin.settings.tabs.users.buttons.newUser')}
        </Button>
      </StyledFlex>
      <br />
      <Typography variant="body1">
        <Trans i18nKey="superadmin.settings.tabs.users.subtitle" />
      </Typography>

      <Box sx={{ mt: 2, mb: 1 }}>
        <CountryPicker
          options={pickerOptions}
          value={countries}
          onChange={setCountries}
          ariaLabel={String(
            t('superadmin.settings.tabs.users.filters.countryLabel')
          )}
          allSelectedLabel={String(
            t('superadmin.settings.tabs.users.filters.allCountries')
          )}
          searchPlaceholder={String(
            t('superadmin.settings.countryPicker.searchPlaceholder')
          )}
          noResultsText={String(
            t('superadmin.settings.countryPicker.noResults')
          )}
          selectedSummaryTemplate={String(
            t('superadmin.settings.countryPicker.selectedSummary')
          )}
          sx={{ minWidth: '180px' }}
        />
      </Box>

      <OptimizedUsersTable countries={countries} />
    </Box>
  );
}
